import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod/v4';

import {
  createGeocodingApi,
  getApiErrorDetail,
  useApiClient,
  useAutocomplete,
} from '@klareospace/pharos-lib-front-utils';
import {
  buildNullablePhoneNumberSchema,
  isBlankOrValidPhone,
} from '@klareospace/pharos-lib-front-utils/phone-validation';
import { applySuggestionToAddressForm } from '@klareospace/pharos-lib-web-utils';
import {
  formatDeliveryAddress,
  normalizeCountry,
  normalizeCountryCode,
  ratioToPercent,
} from '@klareospace/pharos-lib-utils';
import type {
  AddressIdentityKey,
  AutocompleteSuggestion,
  Contact,
  Order,
  PostalAddress,
} from '@klareospace/pharos-lib-types';
import { ADDRESS_IDENTITY_KEYS } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';

import type { UpdateOrderRequest } from '../api';
import type { AddressEditFormInput } from '../schemas/address-edit.schema';
import { useOrderUpdateErrorMessage } from './use-order-update-error-message';
import { useUpdateOrder } from './use-update-order';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AddressEditVariant = 'delivery' | 'pickup';

export interface UseAddressEditFormOptions {
  onSuccess: () => void;
  order: Order;
  variant: AddressEditVariant;
}

type Coordinates = { lat: number; lng: number };

export interface UseAddressEditFormReturn {
  coords: Coordinates | null;
  errorMessage: string | null;
  form: UseFormReturn<AddressEditFormInput>;
  handleBackToSearch: () => void;
  handleEnterManually: () => void;
  handleGeocodeClick: () => void;
  handleMapPositionChange: (lat: number, lng: number) => void;
  handleQueryChange: (query: string) => void;
  handleSuggestionSelect: (suggestion: AutocompleteSuggestion) => void;
  hasAddressForGeocode: boolean;
  isGeocoding: boolean;
  isManualMode: boolean;
  isPending: boolean;
  isSearchLoading: boolean;
  isSelected: boolean;
  onSubmit: () => void;
  query: string;
  score: number | null;
  suggestions: AutocompleteSuggestion[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Normalizes a stored full name/alpha-2/alpha-3 country to the alpha-3 code the `<Select>` expects. */
function resolveAddressDefaults(postalAddress: PostalAddress | undefined): AddressEditFormInput['address'] {
  const defaults: AddressEditFormInput['address'] = {
    additionalInformations: '',
    city: '',
    complement: '',
    country: '',
    digicode: '',
    floor: '',
    hasElevator: false,
    houseNumber: '',
    postalCode: '',
    street: '',
  };
  if (!postalAddress) return defaults;
  const country = normalizeCountryCode(normalizeCountry(postalAddress.country));
  return { ...defaults, ...postalAddress, country };
}

function resolveContactDefaults(
  contact: Order['pickup']['contact'],
): Pick<AddressEditFormInput, 'contactName' | 'contactPhone'> {
  return { contactName: contact?.name ?? '', contactPhone: contact?.phone ?? null };
}

function resolveCoordinatesDefaults(
  location: NonNullable<Order['delivery']['address']>['location'] | undefined,
): Pick<AddressEditFormInput, 'coordinates' | 'geocodingScore' | 'geocodingSource'> {
  if (!location) {
    return { coordinates: {}, geocodingScore: undefined, geocodingSource: undefined };
  }
  const { source } = location.coordinates;
  return {
    coordinates: { latitude: location.coordinates.latitude, longitude: location.coordinates.longitude },
    geocodingScore: ratioToPercent(location.coordinates.score, 0),
    geocodingSource: source === 'autocomplete' || source === 'manual' ? source : undefined,
  };
}

function toAddressEditDefaults(order: Order, variant: AddressEditVariant): AddressEditFormInput {
  const isDelivery = variant === 'delivery';
  const location = isDelivery ? order.delivery.address?.location : undefined;
  const contact = isDelivery ? order.delivery.contact : order.pickup.contact;

  return {
    address: resolveAddressDefaults(location?.address),
    ...resolveContactDefaults(contact),
    ...resolveCoordinatesDefaults(location),
  };
}

type AddressPatch = NonNullable<NonNullable<UpdateOrderRequest['delivery']>['address']>;
type AddressDirtyFields = UseFormReturn<AddressEditFormInput>['formState']['dirtyFields'];

// Touching any identity key triggers the API's merge guard (street/city/postalCode required), so send the whole set.
const SECONDARY_ADDRESS_KEYS = ['additionalInformations', 'complement', 'digicode', 'floor'] as const;
type AddressStringField = AddressIdentityKey | (typeof SECONDARY_ADDRESS_KEYS)[number];

/** Secondary keys are sent only when dirty; core keys are all-or-nothing (country canonicalized). */
function pickDirtyAddressStrings(
  address: AddressEditFormInput['address'],
  dirtyAddress: AddressDirtyFields['address'],
): Partial<Record<AddressStringField, string>> {
  const picked: Partial<Record<AddressStringField, string>> = {};

  const isCoreDirty = ADDRESS_IDENTITY_KEYS.some((field) => dirtyAddress?.[field]);
  if (isCoreDirty) {
    for (const field of ADDRESS_IDENTITY_KEYS) {
      picked[field] = field === 'country' ? normalizeCountry(address[field]) : address[field];
    }
  }

  for (const field of SECONDARY_ADDRESS_KEYS) {
    if (dirtyAddress?.[field]) picked[field] = address[field];
  }

  return picked;
}

/** Coordinates live in a form field distinct from `address` — only included when the map/geocode flow marked it dirty. */
function buildDirtyCoordinatesPatch(
  coordinates: AddressEditFormInput['coordinates'],
  dirtyCoordinates: AddressDirtyFields['coordinates'],
): AddressPatch['coordinates'] {
  const isDirty = Boolean(dirtyCoordinates?.latitude || dirtyCoordinates?.longitude);
  if (!isDirty || coordinates.latitude === undefined || coordinates.longitude === undefined) return undefined;
  return { latitude: coordinates.latitude, longitude: coordinates.longitude };
}

/** Builds the delivery address PATCH; core keys arrive as a complete set, the rest stays dirty-only. */
function buildAddressPatch(values: AddressEditFormInput, dirtyFields: AddressDirtyFields): AddressPatch | undefined {
  const patch: AddressPatch = { ...pickDirtyAddressStrings(values.address, dirtyFields.address) };
  if (dirtyFields.address?.hasElevator) patch.hasElevator = values.address.hasElevator;

  const coordinatesPatch = buildDirtyCoordinatesPatch(values.coordinates, dirtyFields.coordinates);
  if (coordinatesPatch) patch.coordinates = coordinatesPatch;

  return Object.keys(patch).length > 0 ? patch : undefined;
}

function isContactDirty(dirtyFields: AddressDirtyFields): boolean {
  return Boolean(dirtyFields.contactName || dirtyFields.contactPhone);
}

/** Contact is atomic when present (name+phone both mandatory) — dirty with only one of the two filled. */
function isContactPartial(values: AddressEditFormInput): boolean {
  const hasName = values.contactName.trim() !== '';
  const hasPhone = values.contactPhone !== null && values.contactPhone.number !== '';
  return hasName !== hasPhone;
}

/** A present contact phone must be valid for its country — an empty number is handled by `isContactPartial`. */
function isContactPhoneInvalid(values: AddressEditFormInput): boolean {
  return !isBlankOrValidPhone(values.contactPhone);
}

/** `isContactPartial` already excludes the mixed case — this is either a full pair or both cleared (removal). */
function buildContactPatch(values: AddressEditFormInput): Contact | null {
  if (!values.contactName || !values.contactPhone) return null;
  return { name: values.contactName, phone: values.contactPhone };
}

/** Query text shown once a suggestion is selected — house number + street, falling back to the suggestion title. */
function resolveSuggestionQuery(suggestion: AutocompleteSuggestion): string {
  const address = suggestion.address;
  if (!address) return suggestion.title;
  return [address.houseNumber, address.street].filter(Boolean).join(' ') || suggestion.title;
}

/** Builds the PATCH body from dirty fields only; `null` when nothing changed (pickup only ever carries `contact`). */
function buildUpdateBody(
  variant: AddressEditVariant,
  values: AddressEditFormInput,
  dirtyFields: AddressDirtyFields,
): UpdateOrderRequest | null {
  const contactDirty = isContactDirty(dirtyFields);

  if (variant === 'pickup') {
    return contactDirty ? { pickup: { contact: buildContactPatch(values) } } : null;
  }

  const addressPatch = buildAddressPatch(values, dirtyFields);
  if (!addressPatch && !contactDirty) return null;

  const deliveryPatch: NonNullable<UpdateOrderRequest['delivery']> = {};
  if (addressPatch) deliveryPatch.address = addressPatch;
  if (contactDirty) deliveryPatch.contact = buildContactPatch(values);
  return { delivery: deliveryPatch };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Owns the address pop-in's whole state machine: autocomplete search, manual-entry toggle,
 * geocoding, map pin drag, and the submit/error flow. Mirrors the sites feature's
 * use-update-site-form.ts + AddressInput pattern, adapted to the Order address/contact shape.
 * For the pickup variant only the contact fields are sent (pickup has no editable address —
 * it is site-based); the address section is not rendered by the consuming dialog.
 */
export function useAddressEditForm({ onSuccess, order, variant }: UseAddressEditFormOptions): UseAddressEditFormReturn {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('orders');
  const isDelivery = variant === 'delivery';

  const geoClient = useApiClient('geo');
  const geocodingApi = useMemo(() => createGeocodingApi(geoClient), [geoClient]);
  const updateOrder = useUpdateOrder(order.id);
  const resolveErrorMessage = useOrderUpdateErrorMessage(order.id);

  const schema = useMemo(
    () =>
      z.object({
        address: z.object({
          additionalInformations: z.string(),
          city: z.string(),
          complement: z.string(),
          country: z.string(),
          digicode: z.string(),
          floor: z.string(),
          hasElevator: z.boolean(),
          houseNumber: z.string(),
          postalCode: z.string(),
          street: z.string(),
        }),
        contactName: z.string(),
        contactPhone: buildNullablePhoneNumberSchema({ invalid: tCommon('validation.phoneInvalid') }),
        coordinates: z.object({
          latitude: z.number().optional(),
          longitude: z.number().optional(),
        }),
        geocodingScore: z.number().optional(),
        geocodingSource: z.enum(['autocomplete', 'manual']).optional(),
      }),
    [t],
  );
  const defaultValues = useMemo(() => toAddressEditDefaults(order, variant), [order, variant]);

  const form = useForm<AddressEditFormInput>({ defaultValues, resolver: zodResolver(schema) });
  const { setValue } = form;
  // Must be read during render — react-hook-form only recomputes dirtyFields once it's been read.
  const { dirtyFields } = form.formState;

  const initialQuery = isDelivery ? formatDeliveryAddress(order.delivery.address) : '';
  const [query, setQuery] = useState(initialQuery);
  const [isManualMode, setIsManualMode] = useState(defaultValues.geocodingSource === 'manual');
  const [isSelected, setIsSelected] = useState(initialQuery.length > 0);
  const [coords, setCoords] = useState<Coordinates | null>(
    defaultValues.coordinates.latitude !== undefined && defaultValues.coordinates.longitude !== undefined
      ? { lat: defaultValues.coordinates.latitude, lng: defaultValues.coordinates.longitude }
      : null,
  );
  const [score, setScore] = useState<number | null>(defaultValues.geocodingScore ?? null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { isLoading: isSearchLoading, suggestions } = useAutocomplete({
    enabled: isDelivery && !isSelected && !isManualMode,
    query,
  });

  function applyCoords(lat: number, lng: number): void {
    setValue('coordinates.latitude', lat, { shouldDirty: true });
    setValue('coordinates.longitude', lng, { shouldDirty: true });
    setCoords({ lat, lng });
  }

  function applyScore(rawScore: number): void {
    const percent = ratioToPercent(rawScore, 0);
    setScore(percent);
    setValue('geocodingScore', percent);
  }

  const geocodeMutation: UseMutationResult<void, Error, PostalAddress> = useMutation({
    mutationFn: async (address: PostalAddress): Promise<void> => {
      const result = await geocodingApi.geocode(address);
      if ('coords' in result) {
        applyCoords(result.coords.latitude, result.coords.longitude);
        applyScore(result.score);
        return;
      }
      const message = 'message' in result && typeof result.message === 'string' ? result.message : undefined;
      throw new Error(message || tCommon('address.geocodeError'));
    },
    onError: (error: Error) => {
      toast.error(getApiErrorDetail(error).message || tCommon('address.geocodeError'));
    },
  });

  function handleQueryChange(newQuery: string): void {
    setQuery(newQuery);
    if (isSelected) {
      setIsSelected(false);
      setScore(null);
      setValue('geocodingScore', undefined);
      setCoords(null);
    }
  }

  function handleSuggestionSelect(suggestion: AutocompleteSuggestion): void {
    if (geocodeMutation.isPending) return;
    const address = suggestion.address;

    setQuery(resolveSuggestionQuery(suggestion));
    setIsSelected(true);
    setValue('geocodingSource', 'autocomplete');
    applySuggestionToAddressForm<AddressEditFormInput>(suggestion, (field, value) =>
      setValue(field, value as never, { shouldDirty: true }),
    );

    if (suggestion.position) {
      applyCoords(suggestion.position.latitude, suggestion.position.longitude);
      applyScore(1);
      return;
    }
    if (address?.city && address.countryCode) {
      geocodeMutation.mutate({
        city: address.city,
        country: address.countryCode,
        houseNumber: address.houseNumber,
        postalCode: address.postalCode,
        street: address.street,
      });
    }
  }

  function handleEnterManually(): void {
    setIsManualMode(true);
    setQuery('');
  }

  function handleBackToSearch(): void {
    setIsManualMode(false);
    if (form.getValues('address.city')) setIsSelected(true);
  }

  function handleGeocodeClick(): void {
    const values = form.getValues('address');
    if (!values.city || !values.country) return;
    geocodeMutation.mutate(
      {
        city: values.city,
        country: values.country,
        houseNumber: values.houseNumber,
        postalCode: values.postalCode,
        street: values.street,
      },
      {
        onSuccess: (): void => {
          setIsSelected(true);
          setValue('geocodingSource', 'manual');
          setQuery([values.houseNumber, values.street].filter(Boolean).join(' '));
        },
      },
    );
  }

  function handleMapPositionChange(lat: number, lng: number): void {
    applyCoords(lat, lng);
    applyScore(1);
  }

  const addressValues = form.watch('address');
  const hasAddressForGeocode = Boolean(addressValues.street && addressValues.city && addressValues.country);

  function onSubmit(): void {
    setErrorMessage(null);
    const values = form.getValues();

    if (isContactDirty(dirtyFields) && isContactPartial(values)) {
      setErrorMessage(t('drawer.edit.address.contactIncomplete'));
      return;
    }

    if (isContactDirty(dirtyFields) && isContactPhoneInvalid(values)) {
      setErrorMessage(tCommon('validation.phoneInvalid'));
      return;
    }

    const body = buildUpdateBody(variant, values, dirtyFields);
    if (body === null) {
      onSuccess();
      return;
    }
    updateOrder.mutate(body, {
      onError: (error: unknown) => {
        setErrorMessage(resolveErrorMessage(error));
      },
      onSuccess: () => {
        toast.success(
          t(isDelivery ? 'drawer.edit.address.deliverySuccess' : 'drawer.edit.address.pickupContactSuccess'),
        );
        onSuccess();
      },
    });
  }

  return {
    coords,
    errorMessage,
    form,
    handleBackToSearch,
    handleEnterManually,
    handleGeocodeClick,
    handleMapPositionChange,
    handleQueryChange,
    handleSuggestionSelect,
    hasAddressForGeocode,
    isGeocoding: geocodeMutation.isPending,
    isManualMode,
    isPending: updateOrder.isPending,
    isSearchLoading,
    isSelected,
    onSubmit,
    query,
    score,
    suggestions,
  };
}
